import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { AddRounded, CloseRounded } from "@mui/icons-material";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-toastify";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  genders,
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import { useDialog } from "../../../DialogContext";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCases } from "../../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import {
  getAllRoles,
  getAllTags,
  getAllFilterContacts,
} from "../../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { backend_api, backend_url } from "../../../../static/server";
import { getAllCustomField } from "../../../../redux/Apps/Lawyerapp/CustomFieldSlice";
import noOption from "../../../../assets/no-option-found.webp";
import ContactMiniForm from "./ContactMiniForm";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import TagForm from "./TagForm";
import axios from "axios";
import CustomFieldForm from "../../Settings/CustomFields/CustomFieldForm";
import { pageScrollStyle } from "../../../../styles/styles";
import { FILE_TYPES } from "../../../../pages/LawyerPages/Froms/LawyerProfileForm";
import DrawerTemplate from "../../Cases/DrawerTemplate";
import MatterApiField from "../../Settings/FieldsUi/MatterApiField";
import ContactApiField from "../../Settings/FieldsUi/ContactApiField";

const DotStepIcon = ({ active, completed }) => {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: completed || active ? "primary.light" : "#c4c4c4",
      }}
    />
  );
};

export const MAX_FILE_SIZE_MB = 1;

const PersonContactForm = ({
  isEditContactMode,
  editData,
  handleCloseAddContect,
  activeContactType = "people",
}) => {
  // console.log(isEditContactMode, JSON.stringify(editData));
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  // Tags state
  const [tagOptions, setTagOptions] = useState([]);
  const [tagInputValue, setTagInputValue] = useState("");
  const [tagPage, setTagPage] = useState(1);
  const [tagHasMore, setTagHasMore] = useState(true);
  const [tagSearching, setTagSearching] = useState(false);

  // Company state
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyInputValue, setCompanyInputValue] = useState("");
  const [companyPage, setCompanyPage] = useState(1);
  const [companyHasMore, setCompanyHasMore] = useState(true);
  const [companySearching, setCompanySearching] = useState(false);

  // Custom field state
  const [selectedCustomFields, setSelectedCustomFields] = useState([]);
  const { allRoles, allTags, tagLoading, filteredContacts, loading } =
    useSelector((state) => state.contact);
  const { allCustomField } = useSelector((state) => state.customField);

  const contactListType = useMemo(
    () => (activeContactType === "companies" ? "company" : "person"),
    [activeContactType]
  );
  const roleOptions = useMemo(() => allRoles || [], [allRoles]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
    getValues,
    setError,
  } = useForm({
    defaultValues: {
      contact_type: "person",
      company_name: "",
      prefix_name: "",
      first_name: "",
      role: null,
      middle_name: "",
      last_name: "",
      suffix_name: "",
      d_o_b: null,
      gender: "",
      nick_name: "",
      company_self: null,
      title: "",
      tags: [],
      reminder_mail: "",
      mobile: "",
      website_url: "",
      address_1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      custom_fields: [],
      custom_fields_data: [],
      ledes_code: "",
    },
  });

  const custom_fields = watch("custom_fields");
  const [activeIndex, setActiveIndex] = useState(0);
  const steps = [
    {
      label: "Personal Information",
      description: "Basic personal details and profile",
    },
    {
      label: "Contact Information",
      description: "Email, phone, and address details",
    },
    {
      label: "Additional Details",
      description: "Custom fields and LEDES code for extra information",
    },
  ];
  useEffect(() => {
    dispatch(
      getCases({
        url: `${backend_api}case/register/`,
        token: token,
        teamMemberLawyerId,
      })
    );
    dispatch(getAllRoles({ url: `${backend_api}get-all-roles?type=contact` }));
    dispatch(
      getAllCustomField({
        url: `${backend_api}custom-field/?field_for=contact`,
        token: token,
      })
    );

    loadTags(1, "");
    loadCompanies(1, "");
  }, [dispatch, token, teamMemberLawyerId]);

  useEffect(() => {
    if (!isEditContactMode || !editData) return;

    Object.keys(editData)?.forEach((key) => {
      if (key === "d_o_b" && editData[key]) {
        setValue(key, dayjs(editData[key]));
        return;
      }

      if (key === "avatar" && editData[key]) {
        setProfileImage(`${backend_url}/${editData[key]}`);
        return;
      }

      if (key === "company_self" && editData[key]) {
        const compObj =
          typeof editData[key] === "object"
            ? editData[key]
            : companyOptions?.find((c) => c.id === editData[key]) || {
                id: editData[key],
                company_name: editData?.company_name || "",
              };
        setCompanyOptions((prev) => {
          const exist = prev.some((c) => c?.id === compObj?.id);
          if (exist) return prev;
          return [compObj, ...prev];
        });

        setValue(key, compObj?.id);
        return;
      }
      if (key === "role" && editData[key]) {
        const roleId =
          typeof editData[key] === "object" ? editData[key]?.id : editData[key];
        setValue(key, roleId ?? null);
        return;
      }
      if (Array.isArray(editData[key])) {
        if (key === "tags") {
          const savedTags = editData[key].map((tag) =>
            typeof tag === "object"
              ? tag
              : allTags?.find((t) => t?.id === tag) || { id: tag }
          );
          setTagOptions((prev) => {
            const map = new Map();
            savedTags
              ?.filter((tag) => tag?.id)
              ?.forEach((tag) => map.set(tag.id, tag));
            prev?.forEach((tag) => {
              if (tag?.id) {
                map.set(tag.id, { ...map.get(tag.id), ...tag });
              }
            });
            return Array.from(map.values());
          });

          setValue(
            key,
            savedTags?.map((t) => t?.id)
          );
        } else if (key === "custom_fields" && allCustomField) {
          const fieldIds = editData[key]?.map((f) =>
            typeof f === "object" ? f?.id : f
          );
          const orderedCustomFields = fieldIds
            ?.map((id) => allCustomField?.find((field) => field?.id === id))
            ?.filter(Boolean);
          setSelectedCustomFields(orderedCustomFields);

          if (editData?.custom_fields_data) {
            const customFieldDataMap = new Map();
            editData.custom_fields_data.forEach((data) => {
              customFieldDataMap.set(data.id, data);
            });

            const orderedFieldsData = orderedCustomFields?.map((field) => {
              const existing = customFieldDataMap.get(field.id);
              const value = existing ? existing.value : "";
              return {
                id: field.id,
                type: field.field_data?.type,
                value: value,
              };
            });

            setValue("custom_fields_data", orderedFieldsData);
          } else {
            const emptyFieldsData = orderedCustomFields?.map((field) => ({
              id: field?.id,
              type: field.field_data?.type,
              value: "",
            }));
            setValue("custom_fields_data", emptyFieldsData);
          }
          setValue(key, fieldIds);
        } else {
          setValue(key, editData[key]);
        }

        return;
      }

      setValue(key, editData[key]);
    });
  }, [isEditContactMode, editData, allTags, allCustomField, companyOptions]);

  useEffect(() => {
    if (isEditContactMode || !allCustomField || selectedCustomFields.length > 0)
      return;
    const defaultFields = allCustomField?.filter(
      (f) => f?.field_data?.default_apply
    );
    if (defaultFields.length > 0) {
      const ids = defaultFields.map((f) => f.id);
      const data = defaultFields.map((f) => ({
        id: f.id,
        type: f.field_data?.type,
        value: "",
      }));
      setSelectedCustomFields(defaultFields);
      setValue("custom_fields", ids, { shouldValidate: true });
      setValue("custom_fields_data", data);
    }
  }, [allCustomField, selectedCustomFields.length]);

  const mergeTagOptions = useCallback((prev = [], next = [], reset = false) => {
    const map = new Map();
    if (!reset) {
      prev?.forEach((tag) => {
        if (tag?.id) {
          map.set(tag.id, tag);
        }
      });
    }
    next?.forEach((tag) => {
      if (tag?.id) {
        map.set(tag.id, { ...map.get(tag.id), ...tag });
      }
    });
    return Array.from(map.values());
  }, []);

  const loadTags = useCallback(
    async (page = 1, search = "", reset = false) => {
      if (tagSearching) return;

      setTagSearching(true);
      try {
        const response = await dispatch(
          getAllTags({
            url: `${backend_api}info-tags/`,
            token: token,
            page: page,
            pageSize: 20,
            search: search,
            teamMemberLawyerId,
          })
        ).unwrap();

        setTagOptions((prev) =>
          mergeTagOptions(prev, response?.results || [], reset || page === 1)
        );

        setTagHasMore(!!response.next);
      } catch (error) {
        console.error("Error loading tags:", error);
      } finally {
        setTagSearching(false);
      }
    },
    [dispatch, token, tagSearching, teamMemberLawyerId, mergeTagOptions]
  );

  // Load companies with infinite scroll
  const loadCompanies = useCallback(
    async (page = 1, search = "", reset = false) => {
      if (companySearching) return;

      setCompanySearching(true);
      try {
        const response = await dispatch(
          getAllFilterContacts({
            url: `${backend_api}contact/retrieve/?contact_type=company`,
            token: token,
            page: page,
            pageSize: 20,
            teamMemberLawyerId,
          })
        ).unwrap();

        const companies = response.results || [];

        // Filter companies based on search
        const filteredCompanies = search
          ? companies.filter((company) =>
              company.company_name?.toLowerCase().includes(search.toLowerCase())
            )
          : companies;

        setCompanyOptions((prev) => {
          const map = new Map();
          if (!(reset || page === 1)) {
            prev?.forEach((company) => {
              if (company?.id) {
                map.set(company.id, company);
              }
            });
          }
          filteredCompanies.forEach((company) => {
            if (company?.id) {
              map.set(company.id, { ...map.get(company.id), ...company });
            }
          });
          return Array.from(map.values());
        });

        setCompanyHasMore(!!response.next);
      } catch (error) {
        console.error("Error loading companies:", error);
      } finally {
        setCompanySearching(false);
      }
    },
    [dispatch, token, companySearching, teamMemberLawyerId]
  );

  // Handle tag input change with debouncing
  const handleTagInputChange = useCallback(
    (newInputValue) => {
      setTagInputValue(newInputValue);
      setTagPage(1);

      // Debounce search
      const timeoutId = setTimeout(() => {
        loadTags(1, newInputValue, true);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [loadTags]
  );

  // Handle company input change with debouncing
  const handleCompanyInputChange = useCallback(
    (newInputValue) => {
      setCompanyInputValue(newInputValue);
      setCompanyPage(1);

      // Debounce search
      const timeoutId = setTimeout(() => {
        loadCompanies(1, newInputValue, true);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [loadCompanies]
  );

  const selectedTagIds = watch("tags");
  const selectedTags = useMemo(() => {
    if (!Array.isArray(selectedTagIds)) return [];
    const map = new Map();
    tagOptions?.forEach((tag) => {
      if (tag?.id) {
        map.set(tag.id, tag);
      }
    });
    return selectedTagIds.map((id) => map.get(id)).filter(Boolean);
  }, [selectedTagIds, tagOptions]);

  // Handle tag selection
  const handleTagSelectionChange = useCallback(
    (event, newValue, field) => {
      const validTags = (newValue || []).filter((tag) => tag?.id);
      field.onChange(validTags.map((tag) => tag.id));
      setTagOptions((prev) => mergeTagOptions(prev, validTags));
    },
    [mergeTagOptions]
  );

  // Handle company selection
  const handleCompanySelection = useCallback((event, newValue, field) => {
    field.onChange(newValue?.id || null);
    if (newValue?.id) {
      setCompanyOptions((prev) => {
        const exists = prev?.some((option) => option?.id === newValue?.id);
        if (exists) return prev;
        return [newValue, ...(prev || [])];
      });
    }
  }, []);

  const selectedCompanyId = watch("company_self");
  const selectedCompanySelf = useMemo(() => {
    if (!selectedCompanyId) return null;
    return (
      companyOptions?.find((item) => item?.id === selectedCompanyId) || null
    );
  }, [companyOptions, selectedCompanyId]);

  const handleCustomFieldSelectionChange = (event, selectedOptions) => {
    event.preventDefault();

    const currentFieldsData = getValues("custom_fields_data") || [];
    const existingDataMap = new Map(
      currentFieldsData.map((item) => [item.id, item])
    );

    const orderedFieldIds = selectedOptions.map((option) => option.id);
    const orderedFieldsData = selectedOptions.map((option) => {
      const existing = existingDataMap.get(option.id);
      return (
        existing || {
          id: option.id,
          type: option.field_data?.type,
          value: "",
        }
      );
    });

    setValue("custom_fields", orderedFieldIds, { shouldValidate: true });
    setValue("custom_fields_data", orderedFieldsData);
    setSelectedCustomFields(selectedOptions);
  };

  // Handle scroll for tags
  const handleScrollTagList = useCallback(
    (event) => {
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
        if (tagHasMore && !tagSearching) {
          const nextPage = tagPage + 1;
          setTagPage(nextPage);
          loadTags(nextPage, tagInputValue);
        }
      }
    },
    [tagHasMore, tagSearching, tagPage, tagInputValue, loadTags]
  );

  // Handle scroll for companies
  const handleScrollCompanyList = useCallback(
    (event) => {
      const { target } = event;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 5) {
        if (companyHasMore && !companySearching) {
          const nextPage = companyPage + 1;
          setCompanyPage(nextPage);
          loadCompanies(nextPage, companyInputValue);
        }
      }
    },
    [
      companyHasMore,
      companySearching,
      companyPage,
      companyInputValue,
      loadCompanies,
    ]
  );

  const validateCustomFields = (customFields, customFieldsData) => {
    const errors = {};
    if (customFields?.length > 0) {
      customFields.forEach((fieldId, index) => {
        const fieldData = allCustomField?.find((field) => field.id === fieldId);
        if (fieldData?.field_data?.required) {
          const fieldValue = customFieldsData?.[index]?.value;
          if (!fieldValue || fieldValue === "") {
            errors[`custom_fields_data.${index}`] = {
              type: "required",
              message: `${fieldData.field_name} is required`,
            };
          }
        }
      });
    }
    return errors;
  };

  const scrollToFirstError = (errs) => {
    const firstErrorField = Object.keys(errs)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  };

  const handleError = (formErrors) => {
    scrollToFirstError(formErrors);
  };
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const customFieldErrors = validateCustomFields(
      data.custom_fields,
      data.custom_fields_data
    );
    if (Object.keys(customFieldErrors).length > 0) {
      Object.entries(customFieldErrors).forEach(([field, error]) =>
        setError(field, error)
      );
      scrollToFirstError(customFieldErrors);
      setIsSubmitting(false);
      return;
    }
    try {
      let jsonData = { ...data, contact_type: "person" };
      delete jsonData.avatar;
      if (jsonData.d_o_b) {
        jsonData.d_o_b = new Date(jsonData.d_o_b).toISOString();
      }
      if (Array.isArray(jsonData.tags)) {
        jsonData.tags = jsonData.tags
          .map((tag) => Number(tag))
          .filter((tag) => !isNaN(tag));
      }
      if (Array.isArray(jsonData.custom_fields)) {
        jsonData.custom_fields = jsonData.custom_fields
          .map((field) => Number(field))
          .filter((field) => !isNaN(field));
      }
      const url = isEditContactMode
        ? `${backend_api}contact/create/${editData.id}/`
        : `${backend_api}contact/create/`;
      const method = isEditContactMode ? "PUT" : "POST";
      const response = await axios({
        method,
        url,
        data: jsonData,
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      const contactId = response?.data?.data?.id || null;
      toast.success(
        isEditContactMode
          ? "Contact updated successfully"
          : "Contact created successfully"
      );

      dispatch(
        getAllFilterContacts({
          url: `${backend_api}contact/retrieve/?contact_type=${contactListType}`,
          token,
          teamMemberLawyerId,
        })
      );

      if (data.avatar instanceof File) {
        try {
          const formData = new FormData();
          formData.append("avatar", data.avatar, data.avatar.name);
          await axios.put(
            `${backend_api}contact/create/${contactId}/`,
            formData,
            {
              headers: {
                Authorization: `Token ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );
          toast.success("Avatar uploaded successfully");
        } catch (avatarError) {
          toast.error("Failed to upload avatar. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      handleClearForm();
      handleCloseAddContect();
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to create/update contact. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      sessionStorage.removeItem("ContactForm");
      sessionStorage.removeItem("editContactData");
      sessionStorage.removeItem("avatarPreview");
    }
  };

  const handleFileChange = (file) => {
    if (!file) return;

    const fileSizeKB = file.size / 1024;
    const fileSizeMB = fileSizeKB / 1024;

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `File size too large! Maximum allowed size is ${
          MAX_FILE_SIZE_MB * 1024
        } KB. Uploaded file size: ${Math.round(fileSizeKB)} KB.`
      );
      return;
    }

    if (FILE_TYPES.includes(file.type.split("/")[1].toUpperCase())) {
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;
        sessionStorage.setItem("avatarPreview", base64String);
        URL.revokeObjectURL(previewUrl);
        setProfileImage(base64String);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Invalid file type! Please upload a valid image file.");
    }
  };

  const handleDeleteImage = () => {
    setProfileImage(null);
    setValue("avatar", "");
    sessionStorage.removeItem("avatarPreview");
  };

  const handleClearForm = () => {
  };

  return (
    <Box sx={{ height: "93vh", display: "flex", gap: "1rem" }}>
      <Box
        sx={{
          width: "20%",
          p: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          position: "relative",
        }}
      >
        <Stepper
          orientation="vertical"
          activeStep={activeIndex}
          nonLinear
          connector={
            <Box
              sx={{
                ml: 0.5,
                height: "1.4rem",
                borderLeft: "1px dotted #bdbdbd",
              }}
            />
          }
        >
          {steps.map((step, index) => (
            <Step key={index} completed={false}>
              <StepLabel StepIconComponent={DotStepIcon}>
                {step.label}
              </StepLabel>
              <StepContent>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: "0.6rem" }}
                >
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        sx={{
          width: "80%",
          position: "relative",
          overflowY: "auto",
          paddingBottom: "6rem", // Increase from 5rem to 6rem
          ...pageScrollStyle,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, handleError)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                p: "1rem",
              }}
            >
              <Box
                sx={{
                  opacity: 1,
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setActiveIndex(0);
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Personal Details
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        flex: 1,
                      }}
                    >
                      <Controller
                        name="avatar"
                        control={control}
                        render={({ field }) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            {profileImage ? (
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "7rem",
                                  height: "6rem",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  border: "1px dashed #dadada",
                                }}
                              >
                                <img
                                  src={profileImage}
                                  alt="Profile"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onLoad={() => setIsImageLoading(false)}
                                />
                                {isImageLoading && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      bgcolor: "rgba(255,255,255,0.8)",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <CircularProgress size={24} />
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    bgcolor: "rgba(0,0,0,0)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    transition: "background-color 0.3s",
                                    "&:hover": {
                                      bgcolor: "rgba(0,0,0,0.5)",
                                      ".deleteBtn": { display: "flex" },
                                    },
                                  }}
                                >
                                  <IconButton
                                    onClick={handleDeleteImage}
                                    className="deleteBtn"
                                    sx={{
                                      display: "none",
                                      "&:hover": { color: "red" },
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      zIndex: 10,
                                    }}
                                  >
                                    <CloseRounded />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <FileUploader
                                handleChange={(file) => {
                                  handleFileChange(file);
                                  field.onChange(file);
                                }}
                                name="avatar"
                                types={FILE_TYPES}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "1px dashed #dadada",
                                    p: "1rem",
                                    borderRadius: "1rem",
                                    width: "7rem",
                                    height: "6rem",
                                    textAlign: "center",
                                  }}
                                >
                                  <AddPhotoAlternateRoundedIcon
                                    sx={{
                                      fontSize: "1.5rem",
                                      color: "#6D6E6F",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color={
                                      errors?.avatar
                                        ? "error"
                                        : "text.secondary"
                                    }
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    {errors?.avatar
                                      ? "Picture is required"
                                      : "Upload Profile Picture"}
                                  </Typography>
                                </Box>
                              </FileUploader>
                            )}
                          </Box>
                        )}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          flex: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", gap: "1rem" }}>
                          <Box sx={{ display: "flex", gap: "1rem", flex: 1 }}>
                            <Controller
                              name="prefix_name"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Prefix Name"
                                  size="small"
                                  fullWidth
                                />
                              )}
                            />
                            <Controller
                              name="first_name"
                              control={control}
                              rules={{ required: "First Name is required" }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label={
                                    <span>
                                      First Name
                                      <span style={{ color: "red" }}>*</span>
                                    </span>
                                  }
                                  size="small"
                                  fullWidth
                                  error={!!errors.first_name}
                                  helperText={errors.first_name?.message}
                                />
                              )}
                            />
                          </Box>
                          <Box sx={{ display: "flex", gap: "1rem" }}>
                            <Controller
                              name="middle_name"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Middle Name"
                                  size="small"
                                  fullWidth
                                />
                              )}
                            />
                            <Controller
                              name="last_name"
                              control={control}
                              rules={{ required: "Last Name is required" }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label={
                                    <span>
                                      Last Name
                                      <span style={{ color: "red" }}>*</span>
                                    </span>
                                  }
                                  size="small"
                                  fullWidth
                                  error={!!errors.last_name}
                                  helperText={errors.last_name?.message}
                                />
                              )}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: "1rem" }}>
                          <Controller
                            name="suffix_name"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Suffix Name"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="nick_name"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Nick Name"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                          <Controller
                            name="d_o_b"
                            control={control}
                            render={({ field }) => (
                              <DatePicker
                                value={field.value ? dayjs(field.value) : null}
                                onChange={(newValue) => {
                                  const isValidDate =
                                    newValue && dayjs(newValue).isValid();
                                  field.onChange(
                                    isValidDate
                                      ? dayjs(newValue).toISOString()
                                      : null
                                  );
                                }}
                                slotProps={{
                                  textField: {
                                    label: "Date of Birth",
                                    size: "small",
                                    variant: "outlined",
                                    fullWidth: true,
                                  },
                                }}
                                format="MM/DD/YYYY"
                                maxDate={dayjs()}
                              />
                            )}
                          />
                          <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                              <FormControl size="small" fullWidth>
                                <InputLabel
                                  id="gender-label"
                                  sx={{ bgcolor: "#fff", px: "0.3rem" }}
                                >
                                  Gender
                                </InputLabel>
                                <Select {...field} labelId="gender-label">
                                  {genders?.map((item, i) => (
                                    <MenuItem key={i} value={item}>
                                      {item}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Controller
                      name="role"
                      control={control}
                      rules={{
                        required: "Role is required for creating contact",
                      }}
                      render={({ field }) => (
                        <Autocomplete
                          fullWidth
                          options={roleOptions}
                          getOptionLabel={(option) => option?.name || ""}
                          value={
                            roleOptions?.find(
                              (item) => item?.id === field.value
                            ) || null
                          }
                          onChange={(_, newValue) =>
                            field.onChange(newValue?.id || null)
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value?.id
                          }
                          noOptionsText={
                            <NoDataOptionField
                              imgSrc={noOption}
                              text="This role is not available"
                            />
                          }
                          ListboxProps={{
                            sx: pageScrollStyle,
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={
                                <span>
                                  Role<span style={{ color: "red" }}>*</span>
                                </span>
                              }
                              size="small"
                              error={!!errors?.role}
                              helperText={errors?.role?.message}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  bgcolor: "#fff",
                                  borderRadius: "4px",
                                },
                              }}
                            />
                          )}
                        />
                      )}
                    />
                    <Controller
                      name="tags"
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <Autocomplete
                            multiple
                            options={tagOptions}
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.name
                            }
                            value={selectedTags}
                            inputValue={tagInputValue}
                            onInputChange={(_, newInputValue) => {
                              handleTagInputChange(newInputValue);
                            }}
                            onChange={(_, newValue) =>
                              handleTagSelectionChange(_, newValue, field)
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            renderTags={(tagValues, getTagProps) =>
                              tagValues?.map((option, index) => (
                                <Chip
                                  size="small"
                                  key={option.id}
                                  label={option.name}
                                  sx={{ bgcolor: option?.metadata?.color }}
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tag"
                                placeholder="Select tags"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {tagSearching && (
                                        <CircularProgress
                                          size={20}
                                          sx={{ mr: 1 }}
                                        />
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            noOptionsText={
                              <NoDataOptionField
                                imgSrc={noOption}
                                text={"Oops! No Tags to display."}
                                btnText={"Tags"}
                                handleClickNoData={() => {
                                  showDialog(<TagForm />, {
                                    dialogTitle: "Create Tag",
                                  });
                                }}
                              />
                            }
                            ListboxComponent={(props) => (
                              <ListboxComponent
                                {...props}
                                btnText="Add tag"
                                btnIcon={<AddRounded />}
                                handleClikListBtn={() => {
                                  showDialog(<TagForm />, {
                                    dialogTitle: "Create Tag",
                                  });
                                }}
                              />
                            )}
                            ListboxProps={{
                              onScroll: handleScrollTagList,
                              sx: pageScrollStyle,
                            }}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="company_self"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Autocomplete
                            options={companyOptions}
                            getOptionLabel={(option) =>
                              option?.company_name || ""
                            }
                            value={selectedCompanySelf}
                            inputValue={companyInputValue}
                            onInputChange={(_, newInputValue) => {
                              handleCompanyInputChange(newInputValue);
                            }}
                            onChange={(event, newValue) => {
                              handleCompanySelection(event, newValue, field);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Company Name"
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {companySearching && (
                                        <CircularProgress
                                          size={20}
                                          sx={{ mr: 1 }}
                                        />
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            noOptionsText={
                              <NoDataOptionField
                                imgSrc={noOption}
                                text={"Oops! No companies to display."}
                                btnText={"Company"}
                                handleClickNoData={() => {
                                  showDialog(
                                    <ContactMiniForm defaultType="company" />,
                                    {
                                      dialogTitle: "Create company contact",
                                    }
                                  );
                                }}
                              />
                            }
                            ListboxComponent={(props) => (
                              <ListboxComponent
                                {...props}
                                btnText="Add Company"
                                btnIcon={<AddRounded />}
                                handleClikListBtn={() => {
                                  showDialog(
                                    <ContactMiniForm defaultType="company" />,
                                    {
                                      dialogTitle: "Create company contact",
                                    }
                                  );
                                }}
                              />
                            )}
                            ListboxProps={{
                              onScroll: handleScrollCompanyList,
                              sx: pageScrollStyle,
                            }}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography variant="body1">
                                  {option?.company_name}
                                </Typography>
                                <Box>
                                  <BusinessRoundedIcon
                                    sx={{
                                      color: "#2e77f5",
                                      fontSize: "0.9rem",
                                    }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Title"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  opacity: 1,
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setActiveIndex(1);
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Contact Details
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                      <Controller
                        name="reminder_mail"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name="mobile"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Mobile No"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                      <Controller
                        name="website_url"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Website URL"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                        }}
                      >
                        <Controller
                          name="address_1"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              multiline
                              rows={3}
                              fullWidth
                              label="Full Address"
                              placeholder="Enter the full address based on the verified ID."
                            />
                          )}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          width: "100%",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: "1rem" }}>
                          <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="City"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="State"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                        </Box>
                        <Box sx={{ display: "flex", gap: "1rem" }}>
                          <Controller
                            name="pincode"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Pin code"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="country"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Country"
                                size="small"
                                fullWidth
                              />
                            )}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  opacity: 1,
                  p: "1rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 0px 0.8rem #d5d5d5",
                  bgcolor: "#fff",
                  cursor: "pointer",
                  height: "100%",
                }}
                onClick={() => {
                  setActiveIndex(2);
                }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    Custom Fields & Addition info
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: "1rem" }}>
                      <Controller
                        name="custom_fields"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            multiple
                            fullWidth
                            limitTags={4}
                            options={allCustomField || []}
                            getOptionLabel={(option) =>
                              option?.field_name || ""
                            }
                            value={selectedCustomFields}
                            onChange={handleCustomFieldSelectionChange}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Custom Field"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderTags={(tagValues, getTagProps) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  overflowX: "auto",
                                  maxHeight: "2.5rem",
                                  maxWidth: "18rem",
                                }}
                              >
                                {tagValues?.map((option, index) => (
                                  <Chip
                                    size="small"
                                    key={option.id}
                                    label={option.field_name}
                                    {...getTagProps({ index })}
                                  />
                                ))}
                              </Box>
                            )}
                            noOptionsText={
                              <NoDataOptionField
                                imgSrc={noOption}
                                text={
                                  "Oops! Looks like there are no Custom Field to display"
                                }
                                btnText={"Custom Field"}
                                handleClickNoData={() => {
                                  showDialog(<CustomFieldForm />, {
                                    dialogTitle: "Create Custom Field",
                                  });
                                }}
                              />
                            }
                            ListboxComponent={(props) => (
                              <ListboxComponent
                                {...props}
                                btnText="Add Custom Field"
                                btnIcon={<AddRounded />}
                                handleClikListBtn={() => {
                                  showDialog(
                                    <CustomFieldForm
                                      forWhichForm={"contact"}
                                    />,
                                    {
                                      dialogTitle: "Create Custom Field",
                                    }
                                  );
                                }}
                              />
                            )}
                          />
                        )}
                      />
                      <Controller
                        name="ledes_code"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="LEDES Code"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "1rem",
                        width: "100%",
                        maxHeight: "8rem",
                        py: "0.5rem",
                        overflow: "auto",
                        ...pageScrollStyle,
                      }}
                    >
                      {custom_fields && custom_fields?.length > 0 ? (
                        custom_fields?.map((fieldId, i) => {
                          const fieldData = allCustomField?.find(
                            (field) => field?.id === fieldId
                          );
                          if (!fieldData) return null;

                          return (
                            <Box
                              key={fieldData.id}
                              sx={{
                                flex: {
                                  xs: "0 0 100%",
                                  sm: "0 0 calc(50% - 0.5rem)",
                                  md: "0 0 calc(50% - 0.5rem)",
                                },
                                minWidth: 0,
                              }}
                            >
                              <Controller
                                name={`custom_fields_data.${i}`}
                                control={control}
                                rules={{
                                  required: fieldData.field_data?.required,
                                }}
                                defaultValue={{
                                  id: fieldData.id,
                                  type: fieldData.field_data?.type,
                                  value: "",
                                }}
                                render={({ field }) => {
                                  // Handle select type fields
                                  if (fieldData.field_data?.type === "select") {
                                    return (
                                      <FormControl fullWidth>
                                        <Autocomplete
                                          value={field.value?.value || null}
                                          options={
                                            fieldData.field_data?.options || []
                                          }
                                          onChange={(_, newValue) => {
                                            const fieldValue =
                                              newValue?.value || newValue;
                                            field.onChange({
                                              id: fieldData.id,
                                              name: fieldData.field_name,
                                              type: fieldData.field_data?.type,
                                              value: fieldValue,
                                            });
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label={fieldData.field_name}
                                              size="small"
                                              required={
                                                fieldData.field_data?.required
                                              }
                                            />
                                          )}
                                        />
                                      </FormControl>
                                    );
                                  }

                                  if (fieldData.field_data?.type === "date") {
                                    return (
                                      <DatePicker
                                        value={
                                          field.value?.value
                                            ? dayjs(field.value.value)
                                            : null
                                        }
                                        onChange={(newValue) => {
                                          const dateValue = newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : null;
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: dateValue,
                                          });
                                        }}
                                        slotProps={{
                                          textField: {
                                            label: fieldData.field_name,
                                            size: "small",
                                            fullWidth: true,
                                            error: Boolean(
                                              errors?.custom_fields_data?.[i]
                                            ),
                                            helperText:
                                              errors?.custom_fields_data?.[i] &&
                                              `${fieldData.field_name} is required`,
                                          },
                                        }}
                                      />
                                    );
                                  }
                                  if (
                                    fieldData?.field_data?.type ===
                                    "Matter select"
                                  ) {
                                    return (
                                      <MatterApiField
                                        name={`custom_fields_data.${i}`}
                                        label={fieldData.field_name}
                                        value={field.value?.value || null}
                                        onChange={(value) => {
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: value,
                                          });
                                        }}
                                        errors={errors}
                                        token={token}
                                        apiUrl={`${backend_api}case/register/`}
                                        isRequired={
                                          fieldData?.field_data?.required
                                        }
                                        generateUniqueMatterName={(id, name) =>
                                          `000${id}-${name}`
                                        }
                                        AddMatterFunc={({ onCreated }) =>
                                          showDialog(
                                            <DrawerTemplate
                                              onMatterCreated={onCreated}
                                            />,
                                            {
                                              dialogTitle: "Select Template",
                                            }
                                          )
                                        }
                                      />
                                    );
                                  }
                                  if (
                                    fieldData?.field_data?.type ===
                                    "contact select"
                                  ) {
                                    return (
                                      <ContactApiField
                                        name={`custom_fields_data.${i}`}
                                        label={fieldData.field_name}
                                        value={field.value?.value || null}
                                        editData={
                                          field.value?.value
                                            ? { id: field.value.value }
                                            : null
                                        }
                                        onChange={(val) =>
                                          field.onChange({
                                            id: fieldData.id,
                                            name: fieldData.field_name,
                                            type: fieldData.field_data?.type,
                                            value: val,
                                          })
                                        }
                                        errors={errors}
                                        token={token}
                                        apiUrl={`${backend_api}contact/retrieve/`}
                                        isRequired={
                                          fieldData?.field_data?.required
                                        }
                                      />
                                    );
                                  }
                                  // Handle text and other types
                                  return (
                                    <TextField
                                      label={fieldData.field_name}
                                      type={
                                        fieldData.field_data?.type || "text"
                                      }
                                      size="small"
                                      fullWidth
                                      value={field.value?.value || ""}
                                      required={fieldData?.field_data?.required}
                                      onChange={(e) =>
                                        field.onChange({
                                          id: fieldData.id,
                                          name: fieldData.field_name,
                                          type: fieldData.field_data?.type,
                                          value: e.target.value,
                                        })
                                      }
                                      error={Boolean(
                                        errors?.custom_fields_data?.[i]
                                      )}
                                      helperText={
                                        errors?.custom_fields_data?.[i] &&
                                        `${fieldData.field_name} is required`
                                      }
                                    />
                                  );
                                }}
                              />
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem", fontWeight: 400 }}
                        >
                          Select custom Field for additional data.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderTop: "1px solid #FFFFFF2E",
                p: "0.5rem 1rem",
                gap: "1rem",
                zIndex: 10,
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={() => handleClearForm()}
                sx={{ fontSize: "0.8rem" }}
              >
                Clear form
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ fontSize: "0.8rem" }}
                onClick={handleCloseAddContect}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={isSubmitting}
                variant="contained"
                size="small"
                type="submit"
                sx={{ fontSize: "0.8rem" }}
              >
                {isEditContactMode ? "Update person" : "Create People"}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default PersonContactForm;
